const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

export const TIP_MCC_SET = new Set(['5812', '5813']);

export const normalizeAreaKey = (raw = '') =>
  raw
    .toString()
    .trim()
    .toLowerCase()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

export function deriveCityFromObject(obj = {}) {
  const ac = obj.addressComponents || obj.address_components || [];
  if (Array.isArray(ac) && ac.length) {
    const locality =
      ac.find((c) => (c.types || []).includes('locality'))?.long_name ||
      ac.find((c) => (c.types || []).includes('postal_town'))?.long_name;
    if (locality) return normalizeAreaKey(locality);
  }
  const addr =
    obj.formattedAddress ||
    obj.formatted_address ||
    obj.addressText ||
    obj.vicinity ||
    obj.address ||
    '';
  const match = String(addr).match(/^\s*([^,]+)\s*,\s*[A-Z]{2}\b/);
  if (match && match[1]) return normalizeAreaKey(match[1]);
  return '';
}

export function isTipEligibleFromMcc(mcc) {
  if (!mcc) return false;
  const code = String(mcc).replace(/\D/g, '');
  return TIP_MCC_SET.has(code);
}

export function isTipEligibleFromType(primaryType = '') {
  const t = primaryType.toLowerCase();
  if (t.includes('fast_food') || t.includes('meal_takeaway') || t.includes('meal_delivery')) return false;
  return /restaurant|bar|pub|cafe|coffee/.test(t);
}

export function decideTipEligible({ mcc, primaryType } = {}, einDefault = null) {
  if (einDefault === true) return true;
  if (einDefault === false) return false;
  if (isTipEligibleFromMcc(mcc)) return true;
  if (isTipEligibleFromType(primaryType || '')) return true;
  return false;
}

export function normalizePlaceDetails(raw, fallbackMarketKey = null) {
  if (!raw) return null;
  const payload = raw.result && typeof raw.result === 'object' ? raw.result : raw;
  const apiMarketKey = payload.marketKey || raw.marketKey;
  const derived = deriveCityFromObject(payload);
  const effectiveMarketKey = normalizeAreaKey(apiMarketKey || derived || fallbackMarketKey || '');
  const mcc =
    payload.mcc ||
    payload.merchantCategoryCode ||
    payload.merchant_category_code ||
    raw.mcc ||
    '';

  return {
    placeId: payload.placeId || payload.place_id || '',
    placeSource: 'google',
    name: payload.name || payload.displayName || '',
    addressText:
      payload.addressText || payload.formattedAddress || payload.formatted_address || '',
    primaryType: payload.primaryType || '',
    types: Array.isArray(payload.types) ? payload.types : [],
    iconMaskBaseUri: payload.iconMaskBaseUri || '',
    iconBackgroundColor: payload.iconBackgroundColor || '',
    businessStatus: payload.businessStatus || '',
    openNow: typeof payload.openNow === 'boolean' ? payload.openNow : undefined,
    phone: payload.phone || payload.nationalPhoneNumber || payload.formatted_phone_number || '',
    website: payload.website || payload.websiteUri || '',
    hoursText: Array.isArray(payload.hoursText)
      ? payload.hoursText
      : payload.opening_hours?.weekday_text || [],
    addressComponents: payload.addressComponents || payload.address_components || [],
    latitude: typeof payload.latitude === 'number' ? payload.latitude : undefined,
    longitude: typeof payload.longitude === 'number' ? payload.longitude : undefined,
    mcc: mcc ? String(mcc) : '',
    status: 'active',
    exclusionReason: '',
    reopenDate: '',
    marketKey: effectiveMarketKey,
  };
}

export async function fetchPlaceDetails(placeId, marketKey = null) {
  if (!placeId) throw new Error('placeId required');
  const res = await fetch(
    `${API_BASE}/api/places/details?placeId=${encodeURIComponent(placeId)}`,
    {
      credentials: 'include',
    }
  );
  const data = await res.json();
  if (!res.ok) {
    const message = data?.error || data?.message || 'Failed to fetch place details';
    throw new Error(message);
  }
  return normalizePlaceDetails(data, marketKey);
}

export function normalizeAddressComponents(components = []) {
  const out = {};
  components.forEach((component) => {
    if (!component || typeof component !== 'object') return;
    const types = component.types || [];
    if (types.includes('street_number')) out.streetNumber = component.long_name;
    if (types.includes('route')) out.route = component.long_name;
    if (types.includes('locality')) out.city = component.long_name;
    if (types.includes('administrative_area_level_2')) out.county = component.long_name;
    if (types.includes('administrative_area_level_1')) out.state = component.short_name;
    if (types.includes('postal_code')) out.postalCode = component.long_name;
  });
  return out;
}
